/* ===================================== 
 * TESTALB GTM Micro Conversion Tracking 
 * 
 * Version: 1.0
 * Author: david@testlab.gg
 * =====================================
 * 
 * Here is a list of all events generated and the corresponding Event parameters:
 * 
 * ---
 * 
 * ### **1. `image_engagement`**
 * - **Triggered by**: Clicks on media thumbnails, control buttons, or images.
 * - **Event Parameters**:
 *   - `engagement_type`: `"thumbnail_click"`, `"control_<control_name>_click"`, or `"image_click"`.
 *   - `gallery_name`: `"product_details_page"`.
 *   - `image_source` (optional): The `src` of the image.
 *   - `image_alt` (optional): The `alt` text of the image.
 * 
 * ---
 * 
 * ### **2. `other_engagement`**
 * - **Triggered by**: Clicks on links related to product tabs, return policy, or `<details-disclosure>` elements.
 * - **Event Parameters**:
 *   - **For product tab links**:
 *     - `engagement_type`: `"link_click"`.
 *     - `engagement_name`: The name of the product tab.
 *     - `link_url`: The `href` of the link.
 *     - `link_text`: The text content of the link.
 *   - **For return policy links**:
 *     - `engagement_type`: `"link_click"`.
 *     - `engagement_name`: `"return_policy"`.
 *     - `link_url`: The `href` of the link.
 *     - `link_text`: The text content of the link.
 *   - **For `<details-disclosure>` clicks**:
 *     - `engagement_type`: `"disclosure_details_click"`.
 *     - `disclosure_title`: The text content of the `h2.disclosure__title` element.
 * 
 * ---
 * 
 * ### **3. `review_engagement`**
 * - **Triggered by**: Various interactions with reviews, including chips, widgets, thumbs, sorting, searching, pagination, and carousel arrows.
 * - **Event Parameters**:
 *   - **For keyword chip clicks**:
 *     - `engagement_type`: `"keyword_chip_click"`.
 *     - `keyword` (optional): The keyword text from the chip.
 *   - **For widget clicks**:
 *     - `engagement_type`: `"widget_click"`.
 *     - `review_widget`: The widget name.
 *   - **For histogram star clicks**:
 *     - `engagement_type`: `"histogram_star_click"`.
 *     - `star_label`: The `aria-label` of the clicked star.
 *   - **For clear filter button clicks**:
 *     - `engagement_type`: `"clear_filter_click"`.
 *     - `filter_text`: The text content of the clear filter button.
 *   - **For write review link clicks**:
 *     - `engagement_type`: `"write_review_click"`.
 *   - **For review form submissions**:
 *     - `engagement_type`: `"review_form_submit"`.
 *   - **For thumb button clicks**:
 *     - `engagement_type`: `"thumb_click"`.
 *     - `thumb_vote`: `"up"` or `"down"`.
 *     - `review_title`: The title of the review.
 *     - `product_title`: The title of the product.
 *   - **For sorting changes**:
 *     - `engagement_type`: `"sort_change"`.
 *     - `sort_value`: The value of the selected sort option.
 *     - `sort_text`: The text of the selected sort option.
 *   - **For search submissions**:
 *     - `engagement_type`: `"search_submitted"`.
 *     - `search_value`: The value entered in the search box.
 *   - **For pagination clicks**:
 *     - `engagement_type`: `"pagination_first_page_click"`, `"pagination_prev_page_click"`, `"pagination_page_click"`, `"pagination_next_page_click"`, or `"pagination_last_page_click"`.
 *     - `page_number`: The page number clicked.
 *   - **For carousel arrow clicks**:
 *     - `engagement_type`: `"carousel_arrow_click"`.
 *     - `direction`: `"left"` or `"right"`.
 * 
 * ---
 * 
 * ### **4. `time_on_page_<label>`**
 * - **Triggered by**: Time spent on the page (e.g., 30 seconds, 1 minute, etc.).
 * - **Event Parameters**:
 *   - No additional parameters. The event name itself includes the time label (e.g., `time_on_page_30s`, `time_on_page_1m`).
 * 
 * ---
 * 
 * ### **5. `video_engagement`**
 * - **Triggered by**: HTML5 video playback events (start, progress milestones, complete).
 * - **Event Parameters**:
 *   - `video_provider`: `"Shopify"` (for custom HTML5 videos).
 *   - `video_url`: The source URL of the video.
 *   - `video_title`: The title/aria-label of the video, or alt text from associated image.
 *   - `video_duration`: The total duration of the video in seconds.
 *   - `video_current_time`: The current playback time in seconds.
 *   - `video_status`: `"start"`, `"progress"`, or `"complete"`.
 *   - `video_percent` (for progress events): The percentage watched (10, 25, 50, 75, 90, or 100 for complete).
 * 
 */


/**
 * Pushes data to the dataLayer for Google Tag Manager.
 * @param {*} data 
 */
const pushToDataLayer = (data) => {
    if (!window.dataLayer) window.dataLayer = [];
    window.dataLayer.push(data);
};


/**
 * Function to get image SRC under a button.
 * @param {HTMLElement} el - The element
 * @returns {string|null} - The image source or null if not found
 */
function getImageSrc(el) {
    // Try to find an <img> within the element
    let img = el.querySelector('img');
    if (img) return img.src;

    // If not found, try the directly preceding sibling
    let prev = el.previousElementSibling;
    if (prev && prev.tagName && prev.tagName.toLowerCase() === 'img') {
        return prev.src;
    }

    return null;
}
  
/**
 * Function to get image ALT text under a button.
 * @param {HTMLElement} el - The element
 * @returns {string|null} - The image ALT text or null if not found
 */
function getImageAlt(el) {
    const img = el.querySelector('img');
    if (img) return img.alt;

    // If not found, try the directly preceding sibling
    let prev = el.previousElementSibling;
    if (prev && prev.tagName && prev.tagName.toLowerCase() === 'img') {
        return prev.alt;
    }

    return null;
}

/**
 * Tracks all clicks on the page. The following events are tracked:
 * - image_engagement: click on media thumbnail, control button, or image
 * - review_engagement: click on review chips or widgets
 * - other_engagement: click on links related to return policy
 * 
 * Note that only one event is pushed to the dataLayer for each click.
 * 
 * @param {Event} e - The click event
 */
document.addEventListener("click", (e) => {
    const thumbBtn = e.target.closest(".product__thumbnail");
    if (thumbBtn) {
        const src = getImageSrc(thumbBtn);
        const alt = getImageAlt(thumbBtn);
        pushToDataLayer({
            event: "image_engagement",
            engagement_type: "thumbnail_click",
            gallery_name: "product_details_page",
            ...(src && { image_source: src }),
            ...(alt && { image_alt: alt })
        });
        return;
    }

    const controlBtn = e.target.closest(".product-gallery_control_button");
    if (controlBtn) {
        const ctrlName = controlBtn.getAttribute("is")?.toLowerCase()?.replace("next-button", "next")?.replace("previous-button", "previous");    

        pushToDataLayer({
            event: "image_engagement",
            engagement_type: `control${ctrlName ? `_${ctrlName}` : ""}_click`,
            gallery_name: "product_details_page",
        });
        return;
    }
    

    const img = e.target.closest("button[is='media-lightbox-button']");
    if (img) {
        const src = getImageSrc(img);
        const alt = getImageAlt(img);
        pushToDataLayer({
            event: "image_engagement",
            engagement_type: "image_zoom_click",
            gallery_name: "product_details_page",
            ...(src && { image_source: src }),
            ...(alt && { image_alt: alt })
        });
        return;
    }

    const link = e.target.closest("a");
    if (link) {
        const text = link.textContent?.toLowerCase() || "";
        const href = link.getAttribute("href") || "";
        if (
            /returns|return policy|guarantee|satisfaction/.test(text) ||
            /returns|guarantee|satisfaction/.test(href)
        ) {
            pushToDataLayer({
                event: "other_engagement",
                engagement_type: "link_click",
                engagement_name: "return_policy",
                link_url: href,
                link_text: text
            });
            return;
        }
    }

    const detailsDisclosure = e.target.closest("details-disclosure details summary");
    if (detailsDisclosure) {
        const titleElement = detailsDisclosure.querySelector("h2.disclosure__title");
        const titleText = titleElement?.textContent?.trim();
        if (titleText) {
            pushToDataLayer({
                event: "other_engagement",
                engagement_type: "disclosure_details_click",
                disclosure_title: titleText
            });
        }
        return;
    }
});

/*============================== Intercept text_link_click events =============================*/
// Intercept dataLayer.push to catch text_link_click events
(function() {
    if (!window.dataLayer) window.dataLayer = [];
    
    const originalPush = window.dataLayer.push.bind(window.dataLayer);
    
    window.dataLayer.push = function() {
        // Call the original push first
        const result = originalPush.apply(window.dataLayer, arguments);
        
        // Check if the pushed item is a text_link_click event
        console.log(arguments);
        if (arguments.length > 0 && arguments[0] && arguments[0].event === "text_link_click" && arguments[0].link_url?.includes("tab")) {
            const eventData = arguments[0];
            
            // Fire the other_engagement event
            pushToDataLayer({
                event: "other_engagement",
                engagement_type: "tab_link_click",
                link_text: eventData.link_text || "",
                link_url: eventData.link_url || ""
            });
        }
        
        return result;
    };
})();

/*============================== Judgeme Reviews Tracking =============================*/
const reloadReviewListeners = () => {
    console.log("Reloading review listeners...");
    setTimeout(() => waitForReviewThumbs(0), 1200);
    setTimeout(() => waitForReviewPagination(0), 1200);
};
/**
 * Tracks clicks on review chips and widgets. Pushses the following events to the dataLayer:
 * - review_engagement: click on review chips or widgets
 */
const waitForReviewChips = (retries = 0) => {
    const chips = document.querySelectorAll(".jdgm-chip");

    if (chips.length > 0) {
    chips.forEach((chip) => {
        if (!chip.classList.contains("listener-attached")) {
            chip.addEventListener("click", () => {
                const keyword = chip.dataset.text?.trim();
                const widget = chip.closest(".jdgm-widget");
                const widgetType = widget?.dataset.widgetName?.trim() || "review_widget";

                pushToDataLayer({
                    event: "review_engagement",
                    engagement_type: "keyword_chip_click",
                    //review_widget: widgetType,
                    ...(keyword && { keyword: keyword })
                });
                reloadReviewListeners();
            });
            chip.classList.add("listener-attached");
        }
    });
    }

    if ((chips.length === 0) && retries < 20) {
        setTimeout(() => waitForReviewChips(retries + 1), 300);
    }
};

const waitForReviewWidgets = (retries = 0) => {
    const widgets = document.querySelectorAll(".jdgm-widget");

    if (widgets.length > 0) {
        widgets.forEach((widget) => {
            if (!widget.classList.contains("listener-attached") && widget.classList.contains("jdgm-preview-badge")) {
                widget.addEventListener("click", (e) => {
                    const chipInside = e.target.closest(".jdgm-chip");
                    if (!chipInside) {
                        const widgetType = widget.dataset.widgetName?.trim();
                        if (widgetType) {
                            pushToDataLayer({
                                event: "review_engagement",
                                engagement_type: "widget_click",
                                review_widget: widgetType
                            });
                        }
                    }
                });
                widget.classList.add("listener-attached");
            }
        });
    }

    if ((widgets.length === 0) && retries < 20) {
        setTimeout(() => waitForReviewWidgets(retries + 1), 300);
    }
};

const waitForReviewHist = (retries = 0) => {
    // This function waits for the review sort dropdown to be available in the DOM.
    const histogram = document.querySelector(".jdgm-row-stars");
    if (histogram) {
        if (!histogram.classList.contains("listener-attached")) {
            document.addEventListener("click", (e) => {
                const star = e.target.closest(".jdgm-histogram__star");
                if (star) {
                    const starLabel = star.getAttribute("aria-label")?.trim();
                    if (starLabel) {
                        pushToDataLayer({
                            event: "review_engagement",
                            engagement_type: "histogram_star_click",
                            star_label: starLabel
                        });
                        reloadReviewListeners();
                    }
                }
            });
        
            // Track clicks on the clear filter button
            document.addEventListener("click", (e) => {
                const clearFilter = e.target.closest(".jdgm-histogram__clear-filter");
                if (clearFilter) {
                    const filterText = clearFilter.textContent?.trim();
                    if (filterText) {
                        pushToDataLayer({
                            event: "review_engagement",
                            engagement_type: "clear_filter_click",
                            filter_text: filterText
                        });
                        reloadReviewListeners();
                    }
                }
            });
            histogram.classList.add("listener-attached");
        }
    } else if (retries < 20) {
        setTimeout(() => waitForReviewHist(retries + 1), 300);
    }
};

const waitForReviewForm = (retries = 0) => {
    // This function waits for the review form to be available in the DOM.
    const reviewActionWrapper = document.querySelector(".jdgm-widget-actions-wrapper");
    const writeReviewLink = document.querySelector(".jdgm-write-rev-link");
    if (reviewActionWrapper && writeReviewLink) {
        // Track clicks on the write review link
        if (!writeReviewLink.classList.contains("listener-attached")) {
            writeReviewLink.addEventListener("click", (e) => {
                if (writeReviewLink) {
                    pushToDataLayer({
                        event: "review_engagement",
                        engagement_type: "write_review_click"
                    });

                    const reviewForm = document.querySelector(".jdgm-form");
                    // Track form submission after clicking the write review link
                    if (reviewForm && !reviewForm.classList.contains("listener-attached")) {
                        reviewForm.addEventListener("submit", () => {
                            pushToDataLayer({
                                event: "review_engagement",
                                engagement_type: "review_form_submit"
                            });
                        });
                        reviewForm.classList.add("listener-attached");
                    }
                }
            });
            writeReviewLink.classList.add("listener-attached");
        }
    } else if (retries < 20) {
        setTimeout(() => waitForReviewForm(retries + 1), 300);
    }
};

const waitForReviewThumbs = (retries = 0) => {
    const reviewWidgets = document.querySelectorAll("#judgeme_product_reviews");
    const thumbButtons = document.querySelectorAll("#judgeme_product_reviews .jdgm-rev .jdgm-rev__thumb-btn");

    if (reviewWidgets.length > 0 && thumbButtons.length > 0) {
        reviewWidgets.forEach((widget) => {
            const reviews = widget.querySelectorAll(".jdgm-rev");
            reviews.forEach((review) => {
                if (!review.classList.contains("thumb-listener-attached")) {
                    const thumbButtons = review.querySelectorAll(".jdgm-rev__thumb-btn");
                    thumbButtons.forEach((thumbButton) => {
                        thumbButton.addEventListener("click", () => {
                            const voteType = thumbButton.getAttribute("data-thumb-vote"); // "up" or "down"
                            const reviewId = review.getAttribute("data-review-id");
                            const reviewTitle = review.querySelector(".jdgm-rev__title")?.textContent.trim() || "Untitled";
                            const productTitle = review.getAttribute("data-product-title");

                            pushToDataLayer({
                                event: "review_engagement",
                                engagement_type: "thumb_click",
                                thumb_vote: voteType,
                                review_title: reviewTitle,
                                product_title: productTitle
                            });
                        });
                    });

                    review.classList.add("thumb-listener-attached");
                }
            });
        });
    } else if (retries < 20) {
        setTimeout(() => waitForReviewThumbs(retries + 1), 300);
    }
};

const waitForReviewSort = (retries = 0) => {
    // This function waits for the review sort dropdown to be available in the DOM.
    const sortSelect = document.querySelector(".jdgm-sort-dropdown");
    if (sortSelect) {
        if (!sortSelect.classList.contains("listener-attached")) {
            sortSelect.addEventListener("change", () => {
                const selectedOption = sortSelect.options[sortSelect.selectedIndex];
                const sortValue = selectedOption.value;
                const sortText = selectedOption.textContent.trim();

                pushToDataLayer({
                    event: "review_engagement",
                    engagement_type: "sort_change",
                    sort_value: sortValue,
                    sort_text: sortText
                });
                reloadReviewListeners();
            });
            sortSelect.classList.add("listener-attached");
        }
    } else if (retries < 20) {
        setTimeout(() => waitForReviewSort(retries + 1), 300);
    }
};

const waitForReviewSearch = (retries = 0) => {
    // This function waits for the review search input to be available in the DOM.
    const searchInput = document.querySelector(".jdgm-review-search");
    if (searchInput) {
        if (!searchInput.classList.contains("listener-attached")) {
            let eventTriggered = false; // Flag to prevent duplicate events

            const triggerSearchEvent = () => {
                if (!eventTriggered) {
                    const searchValue = searchInput.value.trim();
                    if (searchValue) {
                        pushToDataLayer({
                            event: "review_engagement",
                            engagement_type: "search_submitted",
                            search_value: searchValue
                        });
                        eventTriggered = true; // Set the flag to true
                        setTimeout(() => (eventTriggered = false), 1500); // Reset the flag after a short delay
                        reloadReviewListeners();
                    }
                }
            };

            // Trigger event on "Enter" key press
            searchInput.addEventListener("keydown", (e) => {
                if (e.key === "Enter") {
                    triggerSearchEvent();
                }
            });

            // Trigger event on blur (clicking out of the search box)
            searchInput.addEventListener("blur", () => {
                triggerSearchEvent();
            });
            searchInput.classList.add("listener-attached");
        }
    } else if (retries < 20) {
        setTimeout(() => waitForReviewSearch(retries + 1), 300);
    }
};

const waitForReviewPagination = (retries = 0) => {
    const pagination = document.querySelector(".jdgm-paginate");

    const firstPage = pagination.querySelector(".jdgm-paginate__first-page");
    const prevPage = pagination.querySelector(".jdgm-paginate__prev-page");
    const pageNumbers = pagination.querySelectorAll(".jdgm-paginate__page:not(.jdgm-paginate__first-page):not(.jdgm-paginate__prev-page):not(.jdgm-paginate__next-page):not(.jdgm-paginate__last-page)");
    const nextPage = pagination.querySelector(".jdgm-paginate__next-page");
    const lastPage = pagination.querySelector(".jdgm-paginate__last-page");

    const triggerPageClickEvent = (eventType, pageNumber) => {
        if (eventType && pageNumber) {
            pushToDataLayer({
                event: "review_engagement",
                engagement_type: eventType,
                page_number: pageNumber
            });
            // Reset the pagination listeners to find new pages and buttons
            pagination.classList.remove("pagination-listener-attached");
            reloadReviewListeners();
        }
    };

    //console.log("Checking for review pagination...");
    if (pagination && !pagination.classList.contains("pagination-listener-attached") &&
        ((firstPage || lastPage) && (prevPage || nextPage) && pageNumbers.length > 0 )) {
        //console.log("Review pagination found, attaching listeners...");
        // Track clicks on the first page button
        if (firstPage) {
            firstPage.addEventListener("click", () => {
                triggerPageClickEvent("pagination_first_page_click", firstPage.getAttribute("data-page"));
            });
        }

        // Track clicks on the previous page button
        if (prevPage) {
            prevPage.addEventListener("click", () => {
                triggerPageClickEvent("pagination_prev_page_click", prevPage.getAttribute("data-page"));
            });
        }

        // Track clicks on individual page numbers
        if (pageNumbers.length > 0) {
            pageNumbers.forEach((pageNumber) => {
                pageNumber.addEventListener("click", () => {
                    triggerPageClickEvent("pagination_page_click", pageNumber.getAttribute("data-page"));
                });
            });
        }

        // Track clicks on the next page button
        if (nextPage) {
            nextPage.addEventListener("click", () => {
                triggerPageClickEvent("pagination_next_page_click", nextPage.getAttribute("data-page"));
            });
        }

        // Track clicks on the last page button
        if (lastPage) {
            lastPage.addEventListener("click", () => {
                triggerPageClickEvent("pagination_last_page_click", lastPage.getAttribute("data-page"));
            });
        }

        pagination.classList.add("pagination-listener-attached");
    } else if (retries < 10) {
        setTimeout(() => waitForReviewPagination(retries + 1), 300);
    }
};

const waitForReviewCarouselArrows = (retries = 0) => {
    const carouselWrapper = document.querySelector(".jdgm-carousel-wrapper");

    if (carouselWrapper) {
        const leftArrow = carouselWrapper.querySelector(".jdgm-carousel__left-arrow");
        const rightArrow = carouselWrapper.querySelector(".jdgm-carousel__right-arrow");

        const triggerArrowClickEvent = (direction) => {
            if (direction) {
                pushToDataLayer({
                    event: "review_engagement",
                    engagement_type: "carousel_arrow_click",
                    direction: direction
                });
            }
        };

        if (leftArrow && !leftArrow.classList.contains("listener-attached")) {
            leftArrow.addEventListener("click", () => {
                triggerArrowClickEvent("left");
            });
            leftArrow.classList.add("listener-attached");
        }

        if (rightArrow && !rightArrow.classList.contains("listener-attached")) {
            rightArrow.addEventListener("click", () => {
                triggerArrowClickEvent("right");
            });
            rightArrow.classList.add("listener-attached");
        }
    } else if (retries < 20) {
        setTimeout(() => waitForReviewCarouselArrows(retries + 1), 300);
    }
};

const connectReviewListeners = () => {
    console.log("Connecting review listeners...");
    waitForReviewChips();
    waitForReviewWidgets();
    waitForReviewForm();
    waitForReviewHist();
    waitForReviewThumbs();
    waitForReviewSort();
    waitForReviewSearch();
    waitForReviewPagination();
    waitForReviewCarouselArrows();
};

/*=========================== End of Judgeme Reviews Tracking ==========================*/

const ensureYouTubeIframeQueryParams = () => {
    const requiredOrigin = encodeURIComponent(window.location.origin); // Dynamically get and encode the origin

    const updateIframeSrc = (iframe) => {
        const src = iframe.getAttribute("src");
        if (src && src.includes("youtube.com/embed")) {
            // Check if the required parameters are already present
            if (!src.includes("enablejsapi=1") || !src.includes(`origin=${requiredOrigin}`)) {
                const url = new URL(src);
                const params = new URLSearchParams(url.search);

                // Add the required parameters
                params.set("enablejsapi", "1");
                params.set("origin", window.location.origin);

                // Update the iframe's src attribute
                url.search = params.toString();
                iframe.setAttribute("src", url.toString());
                console.log(`Updated iframe src: ${url.toString()}`);
            } else {
                console.log(`Iframe src already contains required parameters: ${src}`);
            }
        }
    };

    const scanAndFixIframes = () => {
        const iframes = document.querySelectorAll("iframe");

        if (iframes.length > 0) {
            iframes.forEach(updateIframeSrc);
        } else if (iframes.length === 0 && retries < 12) {
            setTimeout(() => scanAndFixIframes(retries + 1), 300);
        }
    };

    // Run the function after the page has fully loaded
    window.addEventListener("load", scanAndFixIframes);
};

const setupTimeOnPageTimers = () => {
    const intervals = [
      { label: '30s', seconds: 30 },
      { label: '1m', seconds: 60 },
      { label: '2m', seconds: 120 },
      { label: '5m', seconds: 300 },
    ];
  
    intervals.forEach(({ label, seconds }) => {
      setTimeout(() => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: `time_on_page_${label}`
        });
        console.log(`GA4 event sent: time_on_page_${label}`);
      }, seconds * 1000);
    });
};

/*============================== Video Tracking =============================*/
/**
 * Gets video metadata from a video element
 * @param {HTMLVideoElement} video - The video element
 * @returns {Object} Video metadata
 */
const getVideoMetadata = (video) => {
    const videoMedia = video.closest('video-media');
    const source = video.querySelector('source');
    const videoUrl = source ? source.src : video.src;
    const videoTitle = video.getAttribute('aria-label') || 
                      videoMedia?.querySelector('img')?.alt || 
                      'Untitled Video';
    
    return {
        url: videoUrl || '',
        title: videoTitle,
        duration: video.duration || 0,
        currentTime: video.currentTime || 0
    };
};

/**
 * Calculates the percentage of video watched
 * @param {HTMLVideoElement} video - The video element
 * @returns {number} Percentage watched (0-100)
 */
const getVideoPercent = (video) => {
    if (!video.duration || video.duration === 0) return 0;
    return Math.round((video.currentTime / video.duration) * 100);
};

/**
 * Tracks video engagement events and pushes them to dataLayer
 * @param {HTMLVideoElement} video - The video element
 */
const trackVideoEngagement = (video) => {
    // Check if this video already has tracking attached
    if (video.dataset.videoTrackingAttached === 'true') {
        return;
    }
    
    video.dataset.videoTrackingAttached = 'true';
    
    // Track which progress milestones have been reached
    const milestonesReached = new Set();
    const progressMilestones = [10, 25, 50, 75, 90];
    
    // Helper function to push video event to dataLayer
    const pushVideoEvent = (status, percent = null) => {
        const metadata = getVideoMetadata(video);
        const eventData = {
            event: 'video_engagement',
            video_provider: 'Shopify',
            video_url: metadata.url,
            video_title: metadata.title,
            video_duration: metadata.duration,
            video_current_time: metadata.currentTime,
            video_status: status
        };
        
        if (percent !== null) {
            eventData.video_percent = percent;
        }
        
        pushToDataLayer(eventData);
        console.log('Video engagement event:', eventData);
    };
    
    // Helper function to handle video start tracking
    const handleVideoStart = () => {
        // Only track start once per video session
        if (!video.dataset.videoStartTracked) {
            pushVideoEvent('start');
            video.dataset.videoStartTracked = 'true';
            // Reset milestones when video starts playing
            milestonesReached.clear();
        }
    };
    
    // Check if video is already playing when we attach the listener
    // This handles autoplay scenarios where the video starts before our listener is attached
    const videoMedia = video.closest('video-media');
    const isAlreadyPlaying = !video.paused && !video.ended && video.currentTime > 0;
    const hasPlayingAttribute = videoMedia && videoMedia.hasAttribute('playing');
    
    if (isAlreadyPlaying || hasPlayingAttribute) {
        // Video is already playing, fire start event immediately
        handleVideoStart();
    }
    
    // Track video start for future play events
    video.addEventListener('play', handleVideoStart);
    
    // Also listen for when the video-media element gets the 'playing' attribute
    // This is how the theme tracks video state, so it's a reliable indicator
    if (videoMedia) {
        const playingObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && 
                    mutation.attributeName === 'playing' && 
                    videoMedia.hasAttribute('playing')) {
                    // Only fire if we haven't already tracked the start
                    if (!video.dataset.videoStartTracked) {
                        handleVideoStart();
                    }
                }
            });
        });
        
        playingObserver.observe(videoMedia, {
            attributes: true,
            attributeFilter: ['playing']
        });
    }
    
    // Track video progress milestones
    video.addEventListener('timeupdate', () => {
        const percent = getVideoPercent(video);
        
        // Check each milestone
        progressMilestones.forEach(milestone => {
            if (percent >= milestone && !milestonesReached.has(milestone)) {
                milestonesReached.add(milestone);
                pushVideoEvent('progress', milestone);
            }
        });
    });
    
    // Track video complete
    video.addEventListener('ended', () => {
        pushVideoEvent('complete', 100);
        // Reset tracking flags for potential replay
        video.dataset.videoStartTracked = '';
        milestonesReached.clear();
    });
    
    // Reset tracking if video is reset/reloaded
    video.addEventListener('loadstart', () => {
        video.dataset.videoStartTracked = '';
        milestonesReached.clear();
    });
};

/**
 * Attaches tracking to a video element if not already tracked
 * @param {HTMLVideoElement} video - The video element
 */
const attachVideoTracking = (video) => {
    if (video && video.dataset.videoTrackingAttached !== 'true') {
        // Wait for metadata to be loaded if not already available
        if (video.readyState >= 1) {
            trackVideoEngagement(video);
            
            // Add a small delay check as a fallback for videos that start playing
            // immediately after being attached (e.g., autoplay on thumbnail click)
            // This catches cases where the video starts between attachment and the initial check
            setTimeout(() => {
                const videoMedia = video.closest('video-media');
                if ((!video.paused && !video.ended && video.currentTime > 0) || 
                    (videoMedia && videoMedia.hasAttribute('playing'))) {
                    // Video started playing, trigger the start tracking if not already done
                    if (!video.dataset.videoStartTracked) {
                        video.dispatchEvent(new Event('play'));
                    }
                }
            }, 150);
        } else {
            video.addEventListener('loadedmetadata', () => {
                trackVideoEngagement(video);
            }, { once: true });
        }
    }
};

/**
 * Waits for video-media elements and attaches tracking to HTML5 videos
 * @param {number} retries - Number of retry attempts
 */
const waitForVideos = (retries = 0) => {
    const videoMediaElements = document.querySelectorAll('video-media:not([host])');
    
    if (videoMediaElements.length > 0) {
        videoMediaElements.forEach((videoMedia) => {
            // Skip if we've already set up observers for this element
            if (videoMedia.dataset.videoObserverAttached === 'true') {
                // Just check if video exists now
                const video = videoMedia.querySelector('video');
                if (video) {
                    attachVideoTracking(video);
                }
                return;
            }
            
            videoMedia.dataset.videoObserverAttached = 'true';
            
            // Check immediately for existing video
            const video = videoMedia.querySelector('video');
            if (video) {
                attachVideoTracking(video);
            }
            
            // Use MutationObserver to watch for when the video is added or when 'loaded' attribute is set
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    // Check if video was added
                    if (mutation.addedNodes.length > 0) {
                        mutation.addedNodes.forEach((node) => {
                            if (node.nodeType === 1) { // Element node
                                const addedVideo = node.tagName === 'VIDEO' ? node : node.querySelector('video');
                                if (addedVideo) {
                                    attachVideoTracking(addedVideo);
                                }
                            }
                        });
                    }
                    // Check if 'loaded' attribute was added (video is now ready)
                    if (mutation.type === 'attributes' && mutation.attributeName === 'loaded') {
                        const video = videoMedia.querySelector('video');
                        if (video) {
                            attachVideoTracking(video);
                        }
                    }
                });
            });
            
            observer.observe(videoMedia, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: ['loaded']
            });
        });
    }
    
    // Retry if no videos found and we haven't exceeded retry limit
    if (videoMediaElements.length === 0 && retries < 20) {
        setTimeout(() => waitForVideos(retries + 1), 300);
    }
};

/*=========================== End of Video Tracking ==========================*/


window.addEventListener('load', function() {
    // Code to run when the entire page is loaded
    //trackIframeFocus();
    console.log("GTM script loaded and event listeners attached.");
});

document.addEventListener('DOMContentLoaded', function() {
    // Code to run when the DOM is ready
    setupTimeOnPageTimers();
    connectReviewListeners();
    ensureYouTubeIframeQueryParams();
    waitForVideos();
  });
